"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, UserRound } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isDevEnvironment } from "@/lib/env";

/**
 * The v2 header: a floating pill nav pinned 14px from the top.
 *
 * `variant="landing"` renders the marketing links (section anchors + beta CTA);
 * `variant="app"` renders the product links (MCP, plus Simple Search for admins)
 * and runs at the wider app measure. Routes still on the v1 language keep the
 * full-width `TopHeader`.
 *
 * Every account affordance (sign in/out, admin, settings) stays reachable
 * through the menu on the right in both variants.
 */
export function PillNav({ variant = "landing" }: { variant?: "landing" | "app" }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAdmin, isMiton, logout } = useAuth();
  // Deferred to avoid an SSR/client hydration mismatch: `isDevEnvironment()`
  // is false on the server and true on the dev hostname client-side.
  const [isDev, setIsDev] = useState(false);
  useEffect(() => {
    setIsDev(isDevEnvironment());
  }, []);

  const loginHref = () => {
    const current =
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "/";
    return `/login?next=${encodeURIComponent(current)}`;
  };

  const goto = (destination: string) => {
    if (user) router.push(destination);
    else router.push(`/login?next=${encodeURIComponent(destination)}`);
  };

  const isApp = variant === "app";
  const on = (href: string) => (pathname.startsWith(href) ? " al-on" : "");
  // Miton people (and only them: the API decides `miton_access` from role AND
  // an @miton.cz address) get a second badge next to the MCP one that opens the
  // /miton hub. Everyone else never sees it.

  return (
    <nav className={`al-nav${isApp ? " al-nav-wide" : ""}`}>
      <Link href="/" className="al-nav-brand">
        <Image src="/branding/alister-symbol.png" alt="" width={32} height={31} priority />
        <span className="al-nav-wm">Alister AI</span>
        {isDev && <span className="env-badge env-dev-only">Dev</span>}
      </Link>

      <span className="al-nav-links">
        <button
          type="button"
          className={`al-nav-badge${isApp ? on("/settings") : ""}`}
          onClick={() => goto("/settings")}
        >
          <i />
          {isApp ? "MCP" : "MCP connector"}
        </button>
        {isMiton && (
          <Link href="/miton" className={`al-nav-badge al-nav-badge-miton${on("/miton")}`}>
            <i />
            Miton Talent
          </Link>
        )}

        {user ? (
          /* Signed in: the badges carry the product. Shortlists, History and
             About are still reachable by URL but are not worth nav space —
             the work happens in the MCP client, not here. */
          <>
            {isAdmin && (
              <Link href="/search" className={`al-nav-link${on("/search")}`}>
                Simple Search
              </Link>
            )}
          </>
        ) : (
          <>
            <a href="/#how" className="al-nav-link">
              How It Works
            </a>
            <a href="/#why" className="al-nav-link">
              Why Alister AI
            </a>
            <Link href="/blog" className="al-nav-link">
              Blog
            </Link>
            <a href="/#beta" className="al-nav-cta">
              Request beta access
            </a>
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="al-nav-menu" aria-label="Open main navigation">
              <Menu size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isApp && !user && (
              <>
                <DropdownMenuItem asChild>
                  <a href="/#how">How It Works</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/#why">Why Alister AI</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/blog">Blog</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/reports">Ecosystem reports</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/#beta">Request beta access</a>
                </DropdownMenuItem>
              </>
            )}
            {user ? (
              <>
                <DropdownMenuItem asChild>
                  <Link href="/settings">MCP Connection</Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link href="/search">Simple Search</Link>
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link href="/admin">Admin</Link>
                  </DropdownMenuItem>
                )}
                {isMiton && (
                  <DropdownMenuItem asChild>
                    <Link href="/miton">Miton Talent</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => logout()}>Sign out</DropdownMenuItem>
                <DropdownMenuItem asChild className="al-nav-menu-account">
                  <Link href="/account">
                    <UserRound size={14} />
                    <span>{user.email}</span>
                  </Link>
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem asChild>
                <Link href={loginHref()}>Sign in</Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </nav>
  );
}
